import { useRef, useState } from "react";
import axios from "axios";
import { Upload, X, Loader2 } from "lucide-react";
import { authHeader } from "@/hooks/useAdminAuth";
import API from "@/lib/api";

export default function ImageUpload({ value, onChange, accept = "*/*" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const handleFile = async (file) => {
    if (!file) return;
    setError("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/upload`, fd, {
        headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
      });
      onChange?.(data.url);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const isImage = value && /\.(jpe?g|png|gif|webp|svg|bmp|avif)(\?|$)/i.test(value);

  return (
    <div>
      <div className="flex gap-2">
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder="Paste a URL or upload a file"
          className="flex-1 bg-[#0A0B10] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#7B2CBF] focus:ring-1 focus:ring-[#7B2CBF] transition-colors"
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-[#7B2CBF]/15 text-[#7B2CBF] border border-[#7B2CBF]/30 hover:bg-[#7B2CBF]/25 disabled:opacity-50 transition-colors shrink-0"
        >
          {uploading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Uploading
            </>
          ) : (
            <>
              <Upload className="w-4 h-4" /> Upload
            </>
          )}
        </button>
        {value && (
          <button
            type="button"
            onClick={() => onChange?.("")}
            className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-400/10 transition-colors shrink-0"
            title="Clear"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={(e) => handleFile(e.target.files?.[0])}
        className="hidden"
      />

      {error && <div className="mt-2 text-xs text-red-400">{error}</div>}

      {value && isImage && (
        <div className="mt-3 inline-block bg-[#0A0B10] border border-white/10 rounded-lg p-2">
          <img src={value} alt="Preview" className="max-h-32 rounded object-cover" />
        </div>
      )}
    </div>
  );
}
